import {
  PopoverButton as HeadlessPopoverButton,
  PopoverButtonProps as HeadlessPopoverButtonProps,
} from "@headlessui/react"
import { ChevronDown } from "lucide-react"
import { ReactNode, useId, RefObject } from "react"

import { classNames } from "../../../../lib/classNames"
import { Label } from "../../../base/informational/Label"
import { Icon, IconSizes } from "../../../Icon"

interface PopoverSelectButtonProps
  extends Omit<HeadlessPopoverButtonProps, "className" | "content"> {
  placeholder: string
  label?: string
  title?: string
  accessory?: ReactNode
  // If defined, content is rendered instead of accessory + title.
  content?: ReactNode
  invalid?: boolean
  errorMessage?: string
  className?: string
  containerClassName?: string
  size?: "small" | "large"
  hideChevron?: boolean
  ref?: RefObject<HTMLButtonElement | null>
}

const PopoverSelectButton = (props: PopoverSelectButtonProps) => {
  const {
    ref,
    placeholder,
    label,
    title,
    accessory,
    content,
    invalid,
    errorMessage,
    className,
    containerClassName,
    disabled,
    size = "large",
    hideChevron,
    ...otherProps
  } = props

  const id = useId()

  return (
    <Label
      label={label}
      inputId={id}
      errorMessage={errorMessage}
      containerClassName={classNames(
        "flex w-full flex-col",
        containerClassName
      )}
    >
      <HeadlessPopoverButton
        id={id}
        className={classNames(
          "bg-input-default border-v2-border-default color-default text-body shadow-control group flex h-10 w-full items-center gap-x-2 rounded border px-3",
          "hover:not-disabled:border-v2-border-strong",
          "disabled:border-v2-border-default-disabled disabled:bg-input-disabled disabled:color-disabled",
          size === "small" && "h-7.5",
          (invalid || !!errorMessage) &&
            "border-v2-border-danger hover:not-disabled:border-v2-border-danger",
          className
        )}
        disabled={disabled}
        {...otherProps}
        ref={ref}
      >
        {content ? (
          <div className="flex min-w-0 flex-1 items-center">{content}</div>
        ) : (
          <>
            {accessory}
            <span
              className={classNames(
                "flex-1 truncate whitespace-nowrap text-left",
                title ? "color-default" : "color-subtler",
                "group-disabled:color-disabled"
              )}
            >
              {title ?? placeholder}
            </span>
          </>
        )}
        {!hideChevron && (
          <Icon
            icon={ChevronDown}
            size={IconSizes.ExtraSmall}
            className="color-icon-subtle group-disabled:color-icon-disabled"
          />
        )}
      </HeadlessPopoverButton>
    </Label>
  )
}

PopoverSelectButton.displayName = "PopoverSelectButton"

export default PopoverSelectButton
